import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  listInventoryLocations,
  saveInventoryLocation,
} from '@/services/inventoryMovements';
import type { InventoryLocationInput } from '@/services/inventoryMovements/types';
import { modulePaths } from '@/routes/paths';
import { useAuth } from '@/shared/hooks/useAuth';

const emptyForm: InventoryLocationInput = {
  name: '',
  description: '',
  active: true,
};

export function LocationsPage() {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('inventory.movements.write');
  const [locations, setLocations] = useState<
    Awaited<ReturnType<typeof listInventoryLocations>>
  >([]);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function reload() {
    setLoading(true);
    try {
      setLocations(await listInventoryLocations());
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canWrite) return;
    setError(null);
    try {
      await saveInventoryLocation(form, editId ?? undefined);
      setForm(emptyForm);
      setEditId(null);
      await reload();
    } catch {
      setError('No se pudo guardar la ubicación.');
    }
  }

  return (
    <section>
      <div className="inventory-page-header">
        <h1>Ubicaciones de inventario</h1>
        <Link to={`${modulePaths.inventory}/movimientos`} className="auth-button auth-button--secondary">
          Movimientos
        </Link>
      </div>

      {canWrite ? (
        <form className="inventory-panel" onSubmit={handleSubmit}>
          <h2>{editId ? 'Editar ubicación' : 'Nueva ubicación'}</h2>
          {error ? <p className="auth-error">{error}</p> : null}
          <div className="users-form-grid">
            <label className="auth-label">
              Nombre *
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>
            <label className="auth-label auth-label--row">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
              />
              Activa
            </label>
          </div>
          <label className="auth-label">
            Descripción
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </label>
          <button type="submit" className="auth-button">
            Guardar
          </button>
        </form>
      ) : null}

      {loading ? <p className="auth-muted">Cargando…</p> : null}
      <table className="inventory-table">
        <thead>
          <tr>
            <th>Nombre</th>
            <th>Activa</th>
            <th>Descripción</th>
          </tr>
        </thead>
        <tbody>
          {locations.map((l) => (
            <tr key={l.id}>
              <td>
                {canWrite ? (
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => {
                      setEditId(l.id);
                      setForm({
                        name: l.name,
                        description: l.description,
                        active: l.active,
                      });
                    }}
                  >
                    {l.name}
                  </button>
                ) : (
                  l.name
                )}
              </td>
              <td>{l.active ? 'Sí' : 'No'}</td>
              <td>{l.description || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
